import { useEffect } from "react";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wrapper cms-pages">
      <div className="title mb-10 text-center">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last Updated: April 9, 2026</p>
      </div>
      <div className="">
        <p>
          Welcome to Smora. We are committed to protecting your privacy and
          ensuring the security of your personal information. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your
          information when you use our mobile application and services.
        </p>

        <h2>1. Information We Collect</h2>
        <p>
          We collect several types of information to provide and improve our
          services:
        </p>

        <h3>1.1 Personal Information</h3>
        <ul>
          <li>Name and contact information (email address, phone number)</li>
          <li>Shipping and billing addresses</li>
          <li>
            Government-issued identity proof (e.g., PAN card, Aadhaar) for
            high-value gold/silver purchases as required by applicable
            regulations
          </li>
          <li>
            Payment information (processed securely through our payment
            partners)
          </li>
          <li>Account credentials and profile information</li>
        </ul>

        <h3>1.2 Transaction Information</h3>
        <ul>
          <li>Purchase history including gold and silver jewellery orders</li>
          <li>Product preferences, saved designs, and wishlist items</li>
          <li>Live gold/silver rate preferences and price alert settings</li>
          <li>Reviews and ratings you provide for purchased jewellery</li>
          <li>
            Making charges, wastage, and GST breakdowns associated with your
            orders
          </li>
        </ul>

        <h3>1.4 Technical Information</h3>
        <ul>
          <li>
            Device information (model, operating system, unique identifiers)
          </li>
          <li>IP address and location data</li>
          <li>App usage data and analytics</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the collected information for the following purposes:</p>

        <ol>
          <li>To process and fulfill your gold and silver jewellery orders</li>
          <li>
            To send real-time gold and silver rate alerts and price updates
          </li>
          <li>
            To verify identity for high-value jewellery purchases as required by
            law
          </li>
          <li>
            To provide customer support, including jewellery-related queries and
            after-sales service
          </li>
          <li>
            To personalize your shopping experience based on metal preferences
            and purchase history
          </li>
          <li>
            To send order updates, shipping notifications, and promotional
            communications
          </li>
          <li>
            To comply with RBI, SEBI, and other applicable regulatory and legal
            obligations
          </li>
          <li>
            To detect and prevent fraudulent transactions and security issues
          </li>
          <li>To improve our app and the jewellery shopping experience</li>
        </ol>

        <h2>3. Information Sharing and Disclosure</h2>
        <p>We may share your information with:</p>

        <h3>3.1 Service Providers</h3>
        <ul>
          <li>
            Payment processors for secure transactions (UPI, card networks, net
            banking)
          </li>
          <li>
            Shipping and logistics partners for insured jewellery delivery
          </li>
          <li>
            BIS-certified hallmarking and third-party quality assurance agencies
          </li>
          <li>Cloud storage and hosting providers</li>
          <li>Analytics and marketing service providers</li>
        </ul>

        <h3>3.3 Legal Requirements</h3>
        <p>
          We may disclose your information if required by law, court order, or
          government regulation, or to protect our rights, property, or safety.
        </p>

        <h2>4. Data Security</h2>
        <p>
          Given the high-value nature of gold and silver jewellery transactions,
          we implement industry-standard security measures to protect your
          personal information, including:
        </p>
        <ul>
          <li>256-bit SSL encryption for all data transmission</li>
          <li>PCI-DSS compliant secure payment gateway integration</li>
          <li>End-to-end encryption for KYC and identity documents</li>
          <li>Regular security audits and penetration testing</li>
          <li>
            Access controls, multi-factor authentication, and role-based
            permissions
          </li>
          <li>
            Encrypted storage of sensitive documents like PAN card and Aadhaar
            details
          </li>
          <li>Data backup and disaster recovery procedures</li>
        </ul>

        <h2>5. Your Rights and Choices</h2>
        <p>
          You have the following rights regarding your personal information:
        </p>

        <h3>5.1 Access and Correction</h3>
        <p>
          You can access and update your account information, including KYC
          details, through the app settings.
        </p>

        <h3>5.2 Data Deletion</h3>
        <p>
          You can request deletion of your account and personal data by
          contacting our support team. Note that certain transaction and KYC
          data may be retained as required by law (e.g., under PMLA, records
          must be maintained for 5 years).
        </p>

        <h3>5.3 Marketing Communications</h3>
        <p>
          You can opt-out of promotional emails and gold/silver price alerts by
          managing your notification preferences in the app.
        </p>

        <h3>5.4 Cookie Preferences</h3>
        <p>You can manage cookie preferences through your device settings.</p>

        <h2>6. Children's Privacy</h2>
        <p>
          Our services are not intended for children under 18 years of age, as
          purchasing jewellery requires legal capacity to enter into binding
          contracts. We do not knowingly collect personal information from
          minors. If you believe we have collected information from a minor,
          please contact us immediately.
        </p>

        <h2>7. Gold & Silver Price Data</h2>
        <p>
          We display live and historical gold and silver market prices sourced
          from reliable market data providers. This data is used solely to
          facilitate jewellery pricing and is not shared with third parties for
          investment or trading purposes.
        </p>

        <h2>8. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries
          other than your country of residence. We ensure appropriate safeguards
          are in place to protect your data in accordance with this Privacy
          Policy and applicable Indian data protection laws.
        </p>

        <h2>9. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify
          you of any material changes by posting the new Privacy Policy on this
          page and updating the "Last Updated" date. Your continued use of our
          services after such changes constitutes acceptance of the updated
          policy.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          If you have any questions or concerns about this Privacy Policy,
          please contact us:
        </p>
        <ul>
          <li>Email: info@smorajewellery.com</li>
          <li>Phone: +91 70692 57283</li>
          <li>Address: Mendarda, Gujarat, India - 362260</li>
        </ul>

        <div className="legal-footer">
          <p>
            By using Smora, you acknowledge that you have read and understood
            this Privacy Policy and agree to its terms.
          </p>
        </div>
      </div>
    </div>
  );
}
